/**
 * All-E eligibility calculator v2.
 * 8-check engine across 4 groups.
 *
 * Group A - Woninggeschiktheid:
 *   Check 0: Hard exclusions (heating type, apartment, renovation)
 *   Check 4: Insulation assessment (advisory)
 *
 * Group B - Warmtecapaciteit:
 *   Check 1: Heat demand capacity at -7C (+ gas cap + energy label gate)
 *
 * Group C - Verwarmingssysteem:
 *   Check 2: Water supply temperature
 *   Check 3: Thermostat day/night behavior
 *
 * Group D - Installatie:
 *   Check 5: Physical installation (space, placement, piping, wall)
 *   Check 6: Electrical capacity (free group, panel)
 *   Check 7: Meerwerk complexity tier (calculated from 5+6)
 */

import {
  REFERENCE_CAPACITY_KW,
  getReferenceCapacityKw,
  FULL_LOAD_HOURS,
  GAS_CALORIFIC_VALUE_KWH,
  DEFAULT_BOILER_EFFICIENCY,
  MAX_TEMP_DELTA,
  DESIGN_OUTDOOR_TEMP,
  BASELINE_INDOOR_TEMP,
  WATER_TEMP_RULES,
  EXCLUSION_HEATING_TYPES,
  COMPATIBLE_HEATING_TYPES,
  GAS_CAP_THRESHOLD_M3,
  LABEL_BLOCK_LIST,
  BUILD_YEAR_RANGES,
  OUTDOOR_UNIT_OPTIONS,
  PIPING_DISTANCE_OPTIONS,
  WALL_MATERIAL_OPTIONS,
  HP_CAPACITY_BY_WATER_TEMP,
  BACKUP_KW,
  FULL_LOAD_HOURS_BY_INSULATION,
  GAS_LHV_FACTOR,
  BOILER_EFFICIENCY,
  RAD_W_M2,
} from "./config";
import { getMessages, type EngineMessages, type Lang } from "./messages";
import { ALL_E_THRESHOLDS, ALL_E_THRESHOLDS_SILENT } from "../heat-demand";
import { computeHeatDemand } from "../heat-demand";
import type {
  AllEInput,
  AllEOutput,
  ExclusionResult,
  HeatDemandResult,
  WaterTempResult,
  ThermostatResult,
  InsulationResult,
  PhysicalResult,
  ElectricalResult,
  MeerwerkResult,
  MeerwerkTier,
  CheckStatus,
  RadiatorCapacityResult,
  RadiatorCapacityRow,
} from "./types";

const HOUSE_TYPE_ORDINALS: Record<string, number> = {
  Vrijstaand: 1,
  "2-onder-1-kap": 2,
  Tussenwoning: 3,
  Hoekwoning: 4,
  Appartement: 5,
};

function hasRichInputs(input: AllEInput): boolean {
  return Boolean(
    input.resident_number !== undefined ||
      input.boiler_age !== undefined ||
      input.hot_water_usage !== undefined ||
      (input.insulation_scores && Object.keys(input.insulation_scores).length > 0),
  );
}

//
// Check 0: Hard exclusions
//
function checkExclusions(input: AllEInput, M: EngineMessages): ExclusionResult {
  const reasons: string[] = [];

  // Heating type exclusions
  for (const ht of input.heating_types) {
    if ((EXCLUSION_HEATING_TYPES as readonly string[]).includes(ht)) {
      if (ht === "Luchtverwarming") reasons.push(M.d.luchtverwarming);
    }
  }

  // Vloerconvectoren only (no compatible type selected)
  const hasConvectoren = input.heating_types.includes("Vloerconvectoren");
  const hasCompatible = input.heating_types.some((ht) =>
    (COMPATIBLE_HEATING_TYPES as readonly string[]).includes(ht)
  );
  if (hasConvectoren && !hasCompatible) {
    reasons.push(M.d.convectoren_only);
  }

  // Apartment checks
  if (input.house_type === "Appartement") {
    if (input.apartment_vve === false) {
      reasons.push(M.d.apartment_no_vve);
    }
    if (input.apartment_outdoor_space === false) {
      reasons.push(M.d.apartment_no_space);
    }
  }

  // Renovation
  if (input.is_renovating) {
    reasons.push(M.d.renovation_block);
  }

  if (reasons.length > 0) {
    return {
      status: "ongeschikt",
      blocked_reasons: reasons,
      detail: reasons.join(" "),
    };
  }

  return {
    status: "geschikt",
    blocked_reasons: [],
    detail: M.no_exclusions,
  };
}

//
// Helpers for the HTML rules engine port (alle-check.html lines 905-1455)
//

/**
 * Map engine inputs to the HTML's insulation bucket (Goed/Gemiddeld/Slecht).
 * Inputs: energy_label, insulation_measures, optional avg insulation_scores.
 *
 *  - "Goed":      label in {A+, A, B} OR insulation_measures has all 4 known items
 *                 OR avg insulation_scores >= 7
 *  - "Slecht":    label in {E, F, G} OR avg insulation_scores < 5 OR
 *                 (no measures AND no label)
 *  - "Gemiddeld": otherwise
 */
function deriveInsulationBucket(input: AllEInput): "Goed" | "Gemiddeld" | "Slecht" {
  const label = (input.energy_label || "").toUpperCase();
  const measures = input.insulation_measures || [];
  const scores = input.insulation_scores ?? {};
  const scoreVals = Object.values(scores).filter((v): v is number => typeof v === "number");
  const avgScore = scoreVals.length > 0 ? scoreVals.reduce((a, b) => a + b, 0) / scoreVals.length : undefined;

  // Goed
  if (["A++++", "A+++", "A++", "A+", "A", "B"].includes(label)) return "Goed";
  if (avgScore !== undefined && avgScore >= 7) return "Goed";
  if (measures.length >= 4) return "Goed";

  // Slecht
  if (["E", "F", "G"].includes(label)) return "Slecht";
  if (avgScore !== undefined && avgScore < 5) return "Slecht";
  if (!label && measures.length === 0 && avgScore === undefined) return "Slecht";

  // C / D / partial measures / mid-range scores -> Gemiddeld
  if (measures.length >= 2) return "Gemiddeld";
  if (["C", "D"].includes(label)) return "Gemiddeld";
  return "Gemiddeld";
}

/**
 * Pick water-supply temp (35/45/55) from React form inputs.
 *
 * HTML (alle-check.html ~lines 1010-1040): numeric cv-temp + manifold-pump
 *   - ufh + no manifold pump -> 35
 *   - ufh + manifold pump    -> 45
 *   - cv_temp_c < 40         -> 35
 *   - cv_temp_c < 50         -> 45
 *   - else                   -> 55
 *
 * Backwards compat: when neither cv_temp_c nor manifold_pump is provided we
 * fall back to the prior heuristic (UFH -> 35, radiators -> 55).
 */
function deriveWaterTemp(input: AllEInput): 35 | 45 | 55 {
  const whole = input.distribution_whole_house || "";
  const ufhActive =
    whole === "Vloerverwarming" || whole === "Vloerverwarming + andere bronnen";

  if (ufhActive) {
    if (input.manifold_pump === true) return 45;
    if (input.manifold_pump === false) return 35;
    // unknown manifold pump -> keep prior default (35)
    return 35;
  }

  if (typeof input.cv_temp_c === "number" && Number.isFinite(input.cv_temp_c)) {
    if (input.cv_temp_c < 40) return 35;
    if (input.cv_temp_c < 50) return 45;
    return 55;
  }

  return 55;
}

//
// Check 1: Heat demand capacity (HTML rules engine port)
//
function checkHeatDemand(input: AllEInput, M: EngineMessages): HeatDemandResult {
  const silent = input.silent_mode_at_night ?? false;
  const referenceCapacityKw = getReferenceCapacityKw(silent);

  // Protective floor #1: gas cap (kept from v8.42; HTML doesn't have it but
  // it prevents nonsense outputs and existing tests rely on it).
  if (input.route === "gas" && input.yearly_gas_m3 > GAS_CAP_THRESHOLD_M3) {
    return {
      status: "ongeschikt",
      energy_demand_kwh: 0,
      required_capacity_kw: 0,
      available_capacity_kw: referenceCapacityKw,
      coverage_pct: 0,
      estimated_max_indoor_temp_c: null,
      gas_cap_exceeded: true,
      detail: M.gas_cap_detail(input.yearly_gas_m3, GAS_CAP_THRESHOLD_M3),
    };
  }

  // Protective floor #2: energy label gate (kept from v8.42).
  const label = input.energy_label?.toUpperCase() || "";
  if (label && (LABEL_BLOCK_LIST as readonly string[]).includes(label)) {
    return {
      status: "ongeschikt",
      energy_demand_kwh: 0,
      required_capacity_kw: 0,
      available_capacity_kw: referenceCapacityKw,
      coverage_pct: 0,
      estimated_max_indoor_temp_c: null,
      label_blocked: true,
      detail: M.label_block_detail(label),
    };
  }

  // ---- HTML rules engine port (alle-check.html lines 905-1455) -----------
  const insulationBucket = deriveInsulationBucket(input);
  const waterTemp = deriveWaterTemp(input);

  // spaceKwh: gas route vs beng1 route
  let spaceKwh: number;
  if (input.route === "gas") {
    const heatingGasM3 = Math.max(0, input.yearly_gas_m3 - input.summer_avg_gas_m3 * 12);
    spaceKwh = heatingGasM3 * GAS_LHV_FACTOR * BOILER_EFFICIENCY;
  } else {
    spaceKwh = input.beng1_value * input.floor_area_m2;
  }

  const s1Hours = FULL_LOAD_HOURS_BY_INSULATION[insulationBucket] ?? 2000;
  const req = spaceKwh / s1Hours;

  // Capacity by water temp + silent mode (HTML lines ~1050-1080).
  const pwrs = HP_CAPACITY_BY_WATER_TEMP[waterTemp];
  const norm = !silent;
  const singlePow = norm ? pwrs.single.normal : (pwrs.single.silent + pwrs.single.normal) / 2;
  const duoPow = norm ? pwrs.duo.normal : (pwrs.duo.silent + pwrs.duo.normal) / 2;
  const singleCap = singlePow + BACKUP_KW;
  const duoCap = duoPow + BACKUP_KW;

  const duoCov = req > 0 ? (duoCap / req) * 100 : 999;
  // HTML: maxTemp = min(duoCov * 27/100 - 7, 20). Cap at 20.
  const maxTempRaw = req > 0 ? Math.min((duoCov * 27) / 100 - 7, 20) : 20;
  const estimatedMaxIndoorTempC = Math.round(maxTempRaw * 10) / 10;

  // Verdict (HTML lines ~1090-1130)
  let status: CheckStatus;
  let recommendedPump: "single" | "duo" | "not_possible";
  let detail: string;

  if (req < singleCap) {
    status = "geschikt";
    recommendedPump = "single";
    detail = M.heat_ok_single(Math.round(req * 100) / 100, Math.round(singleCap * 100) / 100, waterTemp, insulationBucket);
  } else if (req < duoCap) {
    status = "geschikt";
    recommendedPump = "duo";
    detail = M.heat_ok_duo(Math.round(req * 100) / 100, Math.round(duoCap * 100) / 100, waterTemp, insulationBucket);
  } else if (maxTempRaw > 18) {
    status = "extra_controle";
    recommendedPump = "duo";
    detail = M.heat_duo_required(Math.round(duoCov), estimatedMaxIndoorTempC);
  } else if (maxTempRaw > 16) {
    status = "extra_controle";
    recommendedPump = "duo";
    detail = M.heat_duo_limited(Math.round(duoCov), estimatedMaxIndoorTempC);
  } else {
    status = "ongeschikt";
    recommendedPump = "not_possible";
    detail = M.heat_not_suitable(Math.round(duoCov), estimatedMaxIndoorTempC);
  }

  const coveragePct = Math.round(duoCov);

  return {
    status,
    energy_demand_kwh: Math.round(spaceKwh),
    required_capacity_kw: Math.round(req * 100) / 100,
    available_capacity_kw: Math.round(duoCap * 100) / 100,
    coverage_pct: coveragePct,
    estimated_max_indoor_temp_c: estimatedMaxIndoorTempC,
    recommended_pump: recommendedPump,
    detail,
    pipeline: "simple",
  };
}

//
// Check 2: Water supply temperature (+ optional radiator capacity sub-check)
//
// The base lookup is unchanged for backwards compatibility. When the form
// indicates a hot CV system (cv_temp_c > 60 or cv_comfort_55c === false) on
// radiators AND provides per-radiator dimensions, we ALSO compute an exact
// capacity check at W55. mirroring HTML's Step 4 (alle-check.html 1227-1263).
function checkWaterTemperature(
  input: AllEInput,
  requiredCapacityKw: number,
  M: EngineMessages,
): WaterTempResult {
  const mainRoom = input.distribution_main_room;
  const wholeHouse = input.distribution_whole_house;

  // HTML parity: cv_temp_c drives the W35/W45/W55 bucket when provided
  // (lines 1103-1110); manifold_pump drives the floor-heating sub-bucket.
  // deriveWaterTemp encapsulates this.
  const waterTemp = deriveWaterTemp(input);

  const rule = WATER_TEMP_RULES.find(
    (r) => r.cv_comfort_55c === input.cv_comfort_55c && r.distribution === mainRoom
  ) ?? WATER_TEMP_RULES.find(
    (r) => r.cv_comfort_55c === input.cv_comfort_55c && r.distribution === wholeHouse
  );

  const base: WaterTempResult = rule
    ? { status: rule.status, detail: M.tr(rule.detail), water_temp: waterTemp }
    : {
        status: "extra_controle",
        detail: M.water_combo_check(mainRoom, wholeHouse, input.cv_comfort_55c),
        water_temp: waterTemp,
      };

  // Determine whether the optional radiator capacity sub-check applies.
  // HTML parity: cv_temp_c > 60 is the primary "hot CV" signal; fall back to
  // cv_comfort_55c === false only when cv_temp_c is unset.
  const hasCvTemp = typeof input.cv_temp_c === "number" && Number.isFinite(input.cv_temp_c);
  const isHotCv = hasCvTemp
    ? (input.cv_temp_c as number) > 60
    : input.cv_comfort_55c === false;
  const isRadiators = wholeHouse === "Radiatoren";

  if (!isHotCv || !isRadiators) {
    return base;
  }

  const radiators = input.radiators ?? [];
  if (radiators.length === 0) {
    // High CV temp + radiators but no dimensions. keep current behaviour and
    // note that exact controle requires radiator measurements.
    return {
      ...base,
      detail: M.water_fill_radiators(base.detail),
    };
  }

  const coveragePct = input.radiator_coverage_pct ?? 70;

  const perRadiator: RadiatorCapacityRow[] = radiators.map((r) => {
    const wm2 = RAD_W_M2[r.type] ?? 0;
    const lenM = (Number(r.length_cm) || 0) / 100;
    const hgtM = (Number(r.height_cm) || 0) / 100;
    const watts = wm2 * lenM * hgtM;
    return {
      type: r.type,
      length_cm: Number(r.length_cm) || 0,
      height_cm: Number(r.height_cm) || 0,
      kw: Math.round((watts / 1000) * 100) / 100,
    };
  });

  const totalKw =
    Math.round(perRadiator.reduce((sum, r) => sum + r.kw, 0) * 100) / 100;
  const neededKw =
    Math.round(((requiredCapacityKw * coveragePct) / 100) * 100) / 100;
  const capacityPct =
    neededKw > 0 ? Math.round((totalKw / neededKw) * 100) : 0;

  let radStatus: CheckStatus;
  let radDetail: string;
  if (neededKw <= 0) {
    radStatus = "extra_controle";
    radDetail = M.rad_no_data;
  } else if (totalKw >= neededKw) {
    radStatus = "geschikt";
    radDetail = M.rad_ok(totalKw.toFixed(2), neededKw.toFixed(2), capacityPct);
  } else if (capacityPct >= 80) {
    radStatus = "extra_controle";
    radDetail = M.rad_margin(totalKw.toFixed(2), neededKw.toFixed(2), capacityPct);
  } else {
    radStatus = "ongeschikt";
    radDetail = M.rad_insufficient(totalKw.toFixed(2), neededKw.toFixed(2), capacityPct);
  }

  const radiatorCapacity: RadiatorCapacityResult = {
    total_kw: totalKw,
    needed_kw: neededKw,
    coverage_pct: coveragePct,
    capacity_pct: capacityPct,
    status: radStatus,
    per_radiator: perRadiator,
    detail: radDetail,
  };

  // Upgrade the base status if the radiator check is strictly worse.
  const RANK: Record<CheckStatus, number> = {
    geschikt: 0,
    extra_controle: 1,
    ongeschikt: 2,
  };
  const effectiveStatus: CheckStatus =
    RANK[radStatus] > RANK[base.status] ? radStatus : base.status;

  return {
    status: effectiveStatus,
    detail:
      effectiveStatus === radStatus && radStatus !== base.status
        ? radDetail
        : M.rad_combined(base.detail, radDetail),
    water_temp: waterTemp,
    radiator_capacity: radiatorCapacity,
  };
}

//
// Check 3: Thermostat behavior (HTML rules engine port. one-sided delta)
//
// HTML flags (alle-check.html lines ~1300-1370):
//   - day_temp < 17       -> extra_controle (amber, "Dagtemp laag")
//   - day_temp > 22       -> extra_controle (amber, "Dagtemp hoog")
//   - day > night + 2     -> extra_controle (amber, "Delta groot")   ONE-SIDED
//   - (zone-thermostat / extra sources / cw-class flags omitted. no engine input)
// 2026-05-21: ported from HTML v-latest. Previous v8.42 engine used bilateral
// |day-night| > 2 -> ongeschikt; HTML is one-sided + amber.
function checkThermostat(input: AllEInput, M: EngineMessages): ThermostatResult {
  const day = input.day_temp;
  const night = input.night_temp;
  const delta = Math.abs(day - night);

  const amberFlags: string[] = [];
  const redFlags: string[] = [];

  // Zone-thermostaat: NOT a blocker (2026-05-26, Rick). Heating controls work
  // the same for hybrid vs all-e — no fundamental incompatibility. The real
  // downsides surface only when backup power is thin and margins are tight:
  // zone-regeling pushes effective water-temperature higher (lower COP) and
  // adds complexity that makes the system easier to break. Treat as amber:
  // installer should review the case, not auto-reject.
  if (input.zone_thermostat === "ja") {
    amberFlags.push(M.th_zone);
  }
  // HTML amber: extra heat sources.
  if (input.extra_heat_sources === "ja") {
    amberFlags.push(M.th_extra_sources);
  }
  // HTML amber: CW6 tapwaterklasse.
  if (input.cw_class === "CW6") {
    amberFlags.push(M.th_cw6);
  }

  if (day < 17) amberFlags.push(M.th_day_low);
  if (day > 22) amberFlags.push(M.th_day_high);
  if (day > night + 2) amberFlags.push(M.th_delta(day - night));

  let status: CheckStatus;
  let detail: string;
  if (redFlags.length > 0) {
    status = "ongeschikt";
    detail = M.th_not_suitable(redFlags.join("; "));
    if (amberFlags.length > 0) {
      detail += M.th_additional(amberFlags.join("; "));
    }
  } else if (amberFlags.length === 0) {
    status = "geschikt";
    detail = M.th_ok(day, night, delta);
  } else {
    status = "extra_controle";
    detail = M.th_extra_check(amberFlags.join("; "));
  }

  return { status, day_temp: day, night_temp: night, delta, detail };
}

//
// Check 4: Insulation assessment (advisory)
//
function checkInsulation(input: AllEInput, M: EngineMessages): InsulationResult {
  const buildYear = input.build_year || "";
  const label = input.energy_label?.toUpperCase() || "";
  const measures = input.insulation_measures || [];
  const count = measures.length;
  const yearConfig = BUILD_YEAR_RANGES.find((r) => r.value === buildYear);
  const minMeasures = yearConfig?.minMeasures ?? 2;

  // Per-element insulation scores (0-10) take precedence when present.
  const scoresObj = input.insulation_scores ?? {};
  const scoreVals = Object.values(scoresObj).filter((v): v is number => typeof v === "number");
  const avgScore =
    scoreVals.length > 0 ? scoreVals.reduce((a, b) => a + b, 0) / scoreVals.length : undefined;

  let status: CheckStatus = "geschikt";
  let detail = M.ins_default_ok;

  if (avgScore !== undefined) {
    if (avgScore < 5) {
      status = "extra_controle";
      detail = M.ins_score_low(avgScore.toFixed(1));
    } else if (avgScore < 7) {
      status = "geschikt";
      detail = M.ins_score_mid(avgScore.toFixed(1));
    } else {
      status = "geschikt";
      detail = M.ins_score_good(avgScore.toFixed(1));
    }
    if (label && (LABEL_BLOCK_LIST as readonly string[]).includes(label)) {
      status = "extra_controle";
      detail = M.ins_label_despite_score(label, avgScore.toFixed(1));
    }
  } else {
    if (label && (LABEL_BLOCK_LIST as readonly string[]).includes(label)) {
      status = "extra_controle";
      detail = M.ins_label_block(label);
    } else if (buildYear === "voor_1970" && count < 3) {
      status = "extra_controle";
      detail = M.ins_pre1970(count);
    } else if (buildYear && count < minMeasures) {
      status = "extra_controle";
      detail = M.ins_year_min(yearConfig?.label || buildYear, count, minMeasures);
    }
    if (!buildYear && !label && count === 0) {
      detail = M.ins_no_data;
    }
  }

  return {
    status,
    build_year: yearConfig?.label ? M.tr(yearConfig.label) : buildYear || M.unknown,
    energy_label: label || M.unknown,
    insulation_count: count,
    detail,
    avg_insulation_score: avgScore,
    insulation_scores: scoreVals.length > 0 ? scoresObj : undefined,
  };
}

//
// Check 5: Physical installation
//
function checkPhysicalInstallation(input: AllEInput, M: EngineMessages): PhysicalResult {
  const issues: string[] = [];
  let status = "geschikt" as CheckStatus;

  // HeatBattery space
  if (input.heatbattery_space === "nee") {
    status = "ongeschikt";
    issues.push(M.d.heatbattery_space);
  } else if (input.heatbattery_space === "twijfel") {
    if (status !== "ongeschikt") status = "extra_controle";
    issues.push(M.phys_hb_doubt);
  }

  // Outdoor unit placement
  if (input.outdoor_unit_placement === "niet_mogelijk") {
    status = "ongeschikt";
    issues.push(M.d.no_outdoor_placement);
  }

  // Piping distance
  if (input.piping_distance === "meer_dan_20") {
    if (status !== "ongeschikt") status = "extra_controle";
    issues.push(M.d.piping_long);
  }

  // Wall material
  if (input.wall_material === "beton") {
    if (status !== "ongeschikt") status = "extra_controle";
    issues.push(M.d.beton_wall);
  }

  const detail = issues.length > 0
    ? issues.join(" ")
    : M.phys_ok;

  return { status, issues, detail };
}

//
// Check 6: Electrical capacity
//
function checkElectrical(input: AllEInput, M: EngineMessages): ElectricalResult {
  const issues: string[] = [];
  let status: CheckStatus = "geschikt";

  if (input.free_electrical_group === "nee") {
    status = "extra_controle";
    issues.push(M.d.no_electrical_group);
  }

  if (input.panel_replacement_needed === "ja") {
    status = "extra_controle";
    issues.push(M.d.panel_replacement);
  }

  const detail = issues.length > 0
    ? issues.join(" ")
    : M.elec_ok;

  return { status, issues, detail };
}

//
// Check 7: Meerwerk complexity tier (calculated)
//
function calculateMeerwerkTier(input: AllEInput, physical: PhysicalResult, electrical: ElectricalResult, M: EngineMessages): MeerwerkResult {
  const factors: string[] = [];

  // Outdoor unit above ground
  const placement = input.outdoor_unit_placement || "";
  const placementOption = OUTDOOR_UNIT_OPTIONS.find((o) => o.value === placement);
  if (placementOption?.meerwerkFactor) {
    factors.push(M.mw_factor_outdoor(placementOption.label));
  }

  // Piping distance >10m
  const piping = input.piping_distance || "";
  const pipingOption = PIPING_DISTANCE_OPTIONS.find((o) => o.value === piping);
  if (pipingOption?.meerwerkFactor) {
    factors.push(M.mw_factor_piping(pipingOption.label));
  }

  // Beton wall
  const wall = input.wall_material || "";
  const wallOption = WALL_MATERIAL_OPTIONS.find((o) => o.value === wall);
  if (wallOption?.meerwerkFactor) {
    factors.push(M.mw_factor_wall(wallOption.label));
  }

  // No free electrical group
  if (input.free_electrical_group === "nee") {
    factors.push(M.mw_no_group);
  }

  // Panel replacement = counts as 2 factors (instant L-tier per D2C logic)
  if (input.panel_replacement_needed === "ja") {
    factors.push(M.mw_panel);
    factors.push(M.mw_panel_extra);
  }

  const count = factors.length;
  let tier: MeerwerkTier;

  if (count === 0) tier = "XS";
  else if (count === 1) tier = "S";
  else if (count === 2) tier = "M";
  else tier = "L";

  let status: CheckStatus = "geschikt";
  let detail: string;

  if (tier === "XS") {
    detail = M.mw_std;
  } else if (tier === "S") {
    status = "geschikt";
    detail = M.mw_s(factors[0] ?? "");
  } else if (tier === "M") {
    status = "extra_controle";
    detail = M.mw_m(count);
  } else {
    status = "extra_controle";
    detail = M.d.meerwerk_l_tier;
  }

  return {
    status,
    tier,
    factor_count: count,
    factors,
    estimated_surcharge: tier === "XS" ? "€0" : tier === "S" ? "~€400" : tier === "M" ? "~€750" : M.mw_contact,
    detail,
  };
}

//
// Main entry point
//
export function calculateAllEEligibility(input: AllEInput, lang: Lang = "nl"): AllEOutput {
  const M = getMessages(lang);
  // Run all checks
  const check0 = checkExclusions(input, M);
  const check1 = checkHeatDemand(input, M);
  const check2 = checkWaterTemperature(input, check1.required_capacity_kw, M);
  const check3 = checkThermostat(input, M);
  const check4 = checkInsulation(input, M);
  const check5 = checkPhysicalInstallation(input, M);
  const check6 = checkElectrical(input, M);
  const check7 = calculateMeerwerkTier(input, check5, check6, M);

  // Determine overall status
  const allStatuses = [
    check0.status, check1.status, check2.status, check3.status,
    check4.status, check5.status, check6.status, check7.status,
  ];

  let overall: CheckStatus;
  if (allStatuses.includes("ongeschikt")) {
    overall = "ongeschikt";
  } else if (allStatuses.includes("extra_controle")) {
    overall = "extra_controle";
  } else {
    overall = "geschikt";
  }

  // Generate disclaimers
  const disclaimers: string[] = [];

  // Exclusion reasons
  for (const reason of check0.blocked_reasons) {
    disclaimers.push(reason);
  }

  // Gas cap
  if (check1.gas_cap_exceeded) {
    disclaimers.push(M.d.gas_cap);
  }

  // Label block
  if (check1.label_blocked && input.energy_label) {
    disclaimers.push(M.d.label_block(input.energy_label));
  }

  // Constant temp (always for non-ongeschikt)
  if (overall !== "ongeschikt") {
    disclaimers.push(M.d.constant_temp);
  }

  // Partial coverage
  if (check1.status === "extra_controle" && check1.estimated_max_indoor_temp_c != null) {
    disclaimers.push(M.d.partial_coverage(check1.coverage_pct, check1.estimated_max_indoor_temp_c));
  }

  // Water temp warning
  if (check2.status === "extra_controle") {
    disclaimers.push(M.d.water_temp_warning);
  }

  // Thermostat warning
  if (check3.status === "ongeschikt") {
    disclaimers.push(M.d.thermostat_warning);
  }

  // Insulation advisory
  if (check4.status === "extra_controle") {
    disclaimers.push(M.d.insulation_advisory);
  }

  // Physical issues
  for (const issue of check5.issues) {
    if (!disclaimers.includes(issue)) disclaimers.push(issue);
  }

  // Electrical issues
  for (const issue of check6.issues) {
    if (!disclaimers.includes(issue)) disclaimers.push(issue);
  }

  // Meerwerk L-tier
  if (check7.tier === "L") {
    disclaimers.push(M.d.meerwerk_l_tier);
  }

  return {
    check0_exclusions: check0,
    check1_heat_demand: check1,
    check2_water_temp: check2,
    check3_thermostat: check3,
    check4_insulation: check4,
    check5_physical: check5,
    check6_electrical: check6,
    check7_meerwerk: check7,
    overall,
    disclaimers,
  };
}
